/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Stack } from '@mui/material';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormTextField } from 'components/common/form/Form';
import { AddressField } from 'components/common/form/AddressField';
import { LoadingButton } from 'components/common/LoadingButton';

import { editPropertySchema } from '../../validation/form/property';
import { useProperty } from 'hooks/useProperty';
import { useQuote } from 'hooks/useQuote';

export const GenerateQuote = (props) => {
  const { close } = props;

  const { handleUpdateProperty } = useProperty();
  const { handleExportQuote } = useQuote();

  const loading = useSelector((state) => state.marketplace.generateQuoteForm.loading);
  const selectedQuote = useSelector((state) => state.marketplace.selectedQuote);
  const selectedProperty = useSelector((state) => state.properties.selectedProperty);
  const quoteExportLoading = useSelector((state) => state.marketplace.quoteExportLoading);
  const quoteExportDownloadLoading = useSelector((state) => state.marketplace.quoteExportDownloadLoading);
  const selectedQuoteSetProperty = useSelector((state) => state.marketplace.selectedQuoteSetProperty);

  const methods = useForm({
    resolver: yupResolver(editPropertySchema),
    defaultValues: {
      address: {
        address: '',
        suburb: '',
        state: '',
        postcode: '',
        place_id: '',
      },
      vendor_contact: {
        first_name: '',
        last_name: '',
      },
    },
  });

  const handleSave = methods.handleSubmit((data) => {
    const property = {
      address: data?.address,
      vendor_contact: {
        first_name: data?.vendor_contact?.first_name,
        last_name: data?.vendor_contact?.last_name,
      },
    };
    handleUpdateProperty(data.id, property, data.origin, close);
  });

  useEffect(() => {
    if (selectedProperty) {
      methods.reset({ ...selectedProperty });
    } else if (selectedQuoteSetProperty) {
      methods.reset({ ...selectedQuoteSetProperty });
    }
  }, [methods, selectedProperty]);

  return (
    <DrawerContent>
      <DrawerContent.Header title="Generate Quote" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <FormProvider {...methods}>
            <AddressField
              currentAddress={{ value: methods.watch('address') }}
              callbacks={{
                toggleSetValue: (updatedAddress) => {
                  methods.setValue(
                    'address',
                    { ...updatedAddress, place_id: updatedAddress?.place_id ?? '' },
                    { shouldValidate: true }
                  );
                },
              }}
            />
            <FormTextField
              name="vendor_contact.first_name"
              label="Vendor First Name"
              control={methods.control}
            />
            <FormTextField
              name="vendor_contact.last_name"
              label="Vendor Last Name"
              control={methods.control}
            />
          </FormProvider>
          <Stack
            pt={2}
            spacing={2}
          >
            <LoadingButton
              title="EXPORT"
              loading={quoteExportLoading}
              toggleClick={() => handleExportQuote(selectedQuote?.id, false)}
              disabled={quoteExportLoading || quoteExportDownloadLoading || !selectedQuote?.id}
              variant="outlined"
              style={(theme) => ({
                border: `1px solid ${theme.palette.info.main}`,
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.lighter,
                  borderColor: theme.palette.info.main,
                },
                px: 3,
                py: 1.2,
                borderRadius: 1,
              })}
            />
            <LoadingButton
              title="EXPORT AND DOWNLOAD"
              loading={quoteExportDownloadLoading}
              toggleClick={() => handleExportQuote(selectedQuote?.id, true)}
              disabled={quoteExportLoading || quoteExportDownloadLoading || !selectedQuote?.id}
              variant="outlined"
              style={(theme) => ({
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.lighter,
                  borderColor: theme.palette.primary.main,
                },
                px: 3,
                py: 1.2,
                borderRadius: 1,
              })}
            />
          </Stack>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Update"
          submitButtonLoading={loading}
          submit={handleSave}
          close={close}
          disabled={quoteExportLoading || quoteExportDownloadLoading}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
